import type { StoredTgimSession } from '../../tgimBackend/session';

import { OPENIM_API_ADDR, OPENIM_WS_ADDR } from '../../../config';
import { TGIM_OPENIM_PLATFORM_ID_WEB } from '../../tgimBackend/authMethods';
import { callOpenIm, loginOpenIm, resetOpenImLoginLatch } from '../worker/connector';

let logoutOperationCounter = 0;

function assertOpenImLoginOk(result: unknown): void {
  // WASM `login` often resolves with `{ errCode, errMsg }` instead of rejecting. That resolution
  // still latches `loginPromise` as "done", so callers must reset it here or every later
  // `callOpenIm`/`preloadOpenImWasm` call treats this failed attempt as a completed login.
  if (result && typeof result === 'object' && 'errCode' in result) {
    const code = Number((result as { errCode?: unknown }).errCode);
    if (Number.isFinite(code) && code !== 0) {
      const errMsg = String((result as { errMsg?: unknown }).errMsg || 'OpenIM login failed');
      resetOpenImLoginLatch();
      throw new Error(errMsg);
    }
  }
}

export async function initOpenImLogin(session: StoredTgimSession): Promise<{ selfUserId: string }> {
  // The SDK's own `login` performs the WASM boot, database init and `initSDK` call itself, so this
  // is the single entry point rather than a separate `initSDK` + `login` pair.
  const result = await loginOpenIm({
    userID: session.openImUserId,
    token: session.openImToken,
    platformID: TGIM_OPENIM_PLATFORM_ID_WEB,
    apiAddr: OPENIM_API_ADDR,
    wsAddr: OPENIM_WS_ADDR,
  });
  assertOpenImLoginOk(result);

  return { selfUserId: session.openImUserId };
}

/** Best-effort WASM logout. Sign-out must still clear the local tgim session even if this fails. */
export async function logoutOpenIm(): Promise<void> {
  logoutOperationCounter += 1;
  try {
    await callOpenIm('logout', `web-logout-${Date.now()}-${logoutOperationCounter}`);
  } finally {
    resetOpenImLoginLatch();
  }
}
